import {
  FocusEventHandler,
  HTMLInputTypeAttribute,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
} from 'react';

type Props = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  type?: HTMLInputTypeAttribute;
  disabled?: boolean;
  inputProps?: any;
};

export const AutoSizeInput = forwardRef<HTMLInputElement, Props>(
  (
    { value, onBlur, onFocus, onChange, onKeyDown, type, inputProps = {}, disabled = false },
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputWidth, setInputWidth] = useState(0);

    useEffect(() => {
      const animationFrame = requestAnimationFrame(() => {
        if (spanRef.current) {
          setInputWidth(spanRef.current.offsetWidth);
        }
      });

      return () => cancelAnimationFrame(animationFrame);
    }, [value]);

    useEffect(() => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(inputRef.current);
      } else {
        ref.current = inputRef.current;
      }
    }, [ref]);

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
      if (onKeyDown) {
        onKeyDown(event);
      }

      if (event.key === 'Enter' && inputRef.current) {
        inputRef.current.blur();

        event.preventDefault();
      }
    };

    return (
      <div className="relative min-w-0 max-w-full overflow-hidden">
        <span
          ref={spanRef}
          aria-hidden="true"
          className="invisible absolute px-1 font-[inherit] text-xs whitespace-pre"
        >
          {value || ' '}
        </span>
        <input
          disabled={disabled}
          type={type}
          ref={inputRef}
          value={value}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          onChange={(event) => onChange(event.target.value)}
          className="w-fit max-w-full bg-transparent text-xs outline-none"
          style={{ width: `${inputWidth}px` }}
          {...inputProps}
        />
      </div>
    );
  },
);

AutoSizeInput.displayName = 'AutoSizeInput';
